import type { Category, EventId, PlanEvent, Task } from "../../../shared/types";
import type { EventFullData, UpdateEventData } from "../../events/types/event-form-types";
import type { CalendarNavigationState } from "./use-calendar-navigation";
import type {
  FormAnchor,
  PendingRecurrence,
  PendingResync,
} from "./use-week-calendar-state";
import type { RecurrenceToastState } from "../components/dialogs/RecurrenceActionToast";
import type { MobileCalendarViewProps } from "../components/mobile/mobile-calendar-view-types";
import type { DesktopCalendarViewProps } from "../types/desktop-calendar-view";

interface CalendarViewPropsParams {
  nav: CalendarNavigationState;
  today: string;
  displayDays: string[];
  now: Date;
  events: PlanEvent[];
  tasks: Task[];
  eventsByDate: Map<string, PlanEvent[]>;
  tasksByDate: Map<string, Task[]>;
  categoryDotsForDate: Map<string, Set<Category>>;
  selectedEvents: PlanEvent[];
  selectedTasks: Task[];
  editingEvent: PlanEvent | null;
  showAddForm: boolean;
  showFullEditForm: boolean;
  formStartTime: string | null;
  formEndTime: string | null;
  formAnchor: FormAnchor | null;
  activeCategory: Category;
  pendingRecurrence: PendingRecurrence | null;
  pendingResync: PendingResync | null;
  recurrenceToast: RecurrenceToastState | null;
  onHandleEventClick: (event: PlanEvent, anchorRect?: DOMRect) => void;
  onHandleEventLongPress: (event: PlanEvent, anchorRect: DOMRect) => void;
  onHandleSlotClick: (date: string, startTime: string, endTime: string, anchor?: FormAnchor) => void;
  onHandleMobileSlotClick: (startTime: string, endTime: string) => void;
  onHandleUpdateEvent: (
    id: EventId,
    updates: Partial<Pick<PlanEvent, "date" | "startTime" | "endTime">>,
  ) => void;
  onHandleOpenAdd: () => void;
  onSetEditingEvent: (v: PlanEvent | null) => void;
  onAddEvent: (
    title: string,
    category: Category,
    date: string,
    startTime: string,
    endTime: string,
  ) => void;
  onAddEventFull?: (data: EventFullData) => void;
  onDeleteEvent: (id: EventId) => void;
  onUpdateEvent?: (id: EventId, updates: UpdateEventData) => void;
  onSetShowAddForm: (v: boolean) => void;
  onSetFormStartTime: (v: string | null) => void;
  onSetFormEndTime: (v: string | null) => void;
  onSetShowFullEditForm: (v: boolean) => void;
  onSetPendingResync: (v: PendingResync | null) => void;
  onSetRecurrenceToast: (v: RecurrenceToastState | null) => void;
  onHandleDuplicate: (event: PlanEvent) => void;
  onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  onUndoEvent?: () => void;
  onCloseAddForm: () => void;
  onHandleRecurrenceChoice: (action: "this" | "all") => void;
  onHandleResyncChoice: (action: "this" | "all") => void;
  onSetPendingRecurrence: (v: PendingRecurrence | null) => void;
  readOnly?: boolean;
  onToggleReadOnly?: () => void;
}

export function useCalendarViewProps(params: CalendarViewPropsParams) {
  const mobileProps: MobileCalendarViewProps = {
    nav: params.nav,
    today: params.today,
    displayDays: params.displayDays,
    selectedDate: params.nav.selectedDate,
    categoryDotsForDate: params.categoryDotsForDate,
    eventsByDate: params.eventsByDate,
    tasksByDate: params.tasksByDate,
    selectedEvents: params.selectedEvents,
    selectedTasks: params.selectedTasks,
    now: params.now,
    showAddForm: params.showAddForm,
    formStartTime: params.formStartTime,
    formEndTime: params.formEndTime,
    activeCategory: params.activeCategory,
    onHandleEventClick: params.onHandleEventClick,
    onHandleMobileSlotClick: params.onHandleMobileSlotClick,
    onHandleOpenAdd: params.onHandleOpenAdd,
    onAddEvent: params.onAddEvent,
    onAddEventFull: params.onAddEventFull,
    onDeleteEvent: params.onDeleteEvent,
    onUpdateEvent: params.onUpdateEvent,
    onSetShowAddForm: params.onSetShowAddForm,
    onSetFormStartTime: params.onSetFormStartTime,
    onSetFormEndTime: params.onSetFormEndTime,
    onCloseAddForm: params.onCloseAddForm,
    pendingRecurrence: params.pendingRecurrence,
    pendingResync: params.pendingResync,
    recurrenceToast: params.recurrenceToast,
    onHandleRecurrenceChoice: params.onHandleRecurrenceChoice,
    onHandleResyncChoice: params.onHandleResyncChoice,
    onSetPendingRecurrence: params.onSetPendingRecurrence,
    readOnly: params.readOnly,
    onToggleReadOnly: params.onToggleReadOnly,
    editing: {
      editingEvent: params.editingEvent,
      events: params.events,
      showFullEditForm: params.showFullEditForm,
      onSetEditingEvent: params.onSetEditingEvent,
      onSetShowFullEditForm: params.onSetShowFullEditForm,
      onSetPendingResync: params.onSetPendingResync,
      onSetRecurrenceToast: params.onSetRecurrenceToast,
      onHandleDuplicate: params.onHandleDuplicate,
      onAddRecurrenceException: params.onAddRecurrenceException,
      onUndoEvent: params.onUndoEvent,
    },
  };

  const desktopProps: DesktopCalendarViewProps = {
    nav: params.nav,
    today: params.today,
    displayDays: params.displayDays,
    now: params.now,
    events: params.events,
    tasks: params.tasks,
    eventsByDate: params.eventsByDate,
    tasksByDate: params.tasksByDate,
    selectedEvents: params.selectedEvents,
    editingEvent: params.editingEvent,
    showAddForm: params.showAddForm,
    showFullEditForm: params.showFullEditForm,
    selectedDate: params.nav.selectedDate,
    formStartTime: params.formStartTime,
    formEndTime: params.formEndTime,
    formAnchor: params.formAnchor,
    activeCategory: params.activeCategory,
    pendingRecurrence: params.pendingRecurrence,
    pendingResync: params.pendingResync,
    recurrenceToast: params.recurrenceToast,
    onHandleEventClick: params.onHandleEventClick,
    onHandleEventLongPress: params.onHandleEventLongPress,
    onHandleSlotClick: params.onHandleSlotClick,
    onHandleUpdateEvent: params.onHandleUpdateEvent,
    onHandleOpenAdd: params.onHandleOpenAdd,
    onSetEditingEvent: params.onSetEditingEvent,
    onAddEvent: params.onAddEvent,
    onAddEventFull: params.onAddEventFull,
    onDeleteEvent: params.onDeleteEvent,
    onUpdateEvent: params.onUpdateEvent,
    onSetShowFullEditForm: params.onSetShowFullEditForm,
    onSetPendingResync: params.onSetPendingResync,
    onSetRecurrenceToast: params.onSetRecurrenceToast,
    onHandleDuplicate: params.onHandleDuplicate,
    onAddRecurrenceException: params.onAddRecurrenceException,
    onUndoEvent: params.onUndoEvent,
    onCloseAddForm: params.onCloseAddForm,
    onHandleRecurrenceChoice: params.onHandleRecurrenceChoice,
    onHandleResyncChoice: params.onHandleResyncChoice,
    onSetPendingRecurrence: params.onSetPendingRecurrence,
    readOnly: params.readOnly,
    onToggleReadOnly: params.onToggleReadOnly,
  };

  return { mobileProps, desktopProps };
}
